import { FieldErrors } from "react-hook-form";
import { FormData, ProviderSchema, AvailableProvider } from "../types";
import {
  getSchemaConstValue,
  getVisibleCredentialFieldKeys,
  isConstSchemaProperty,
  shouldShowMerchantIdField,
} from "./schemaConstFields";

export function isPriorityEditable(
  schema: ProviderSchema | undefined,
): boolean {
  const priorityProperty = schema?.properties?.priority;
  if (!priorityProperty) return true;
  if (isConstSchemaProperty(priorityProperty)) {
    return false;
  }
  const { minimum, maximum } = priorityProperty;
  return minimum !== maximum;
}

export function getPriorityFixedValue(
  schema: ProviderSchema | undefined,
): number {
  const priorityProperty = schema?.properties?.priority;
  if (!priorityProperty) return 1;
  if (isConstSchemaProperty(priorityProperty)) {
    return Number(getSchemaConstValue(priorityProperty));
  }
  const { minimum, maximum } = priorityProperty;
  if (minimum !== undefined && minimum === maximum) {
    return minimum;
  }
  return 1;
}

export function areBrandsRequired(schema: ProviderSchema | undefined): boolean {
  const acquirerRequired = schema?.properties?.acquirer?.required || [];
  return acquirerRequired.includes("brands");
}

export function hasBrandsInSchema(schema: ProviderSchema | undefined): boolean {
  return !!schema?.properties?.acquirer?.properties?.brands;
}

export function hasCredentialsInSchema(
  schema: ProviderSchema | undefined,
): boolean {
  return getVisibleCredentialFieldKeys(schema).length > 0;
}

export function areCredentialsRequired(
  schema: ProviderSchema | undefined,
): boolean {
  return (schema?.required || []).includes("credentials");
}

export function hasMerchantIdInSchema(
  schema: ProviderSchema | undefined,
): boolean {
  return shouldShowMerchantIdField(schema);
}

export function getAvailableMethods(
  schema: ProviderSchema | undefined,
): string[] {
  const methodsEnum = schema?.properties?.methods?.items?.enum;
  return methodsEnum || [];
}

export function shouldShowMcc(schema: ProviderSchema | undefined): boolean {
  const methods = getAvailableMethods(schema);
  return methods.some((m) => m === "Credit" || m === "Debit");
}

export function getAvailableBrands(
  schema: ProviderSchema | undefined,
): string[] {
  const brandsEnum =
    schema?.properties?.acquirer?.properties?.brands?.items?.enum;
  return brandsEnum || [];
}

export function getDefaultMethods(
  schema: ProviderSchema | undefined,
): string[] {
  const availableMethods = getAvailableMethods(schema);
  return availableMethods.length > 0 ? availableMethods : ["Credit"];
}

export function filterSchemaBasedErrors(
  errors: FieldErrors<FormData> | undefined,
  schema: ProviderSchema | undefined,
): FieldErrors<FormData> {
  if (!errors) return {};
  const filtered = { ...errors };

  if (!shouldShowMerchantIdField(schema)) {
    delete filtered.merchantId;
  }

  if (!hasBrandsInSchema(schema)) {
    delete filtered.brands;
  }

  if (
    filtered.credentials &&
    typeof filtered.credentials === "object" &&
    schema?.properties?.credentials?.properties
  ) {
    const credentialErrors = { ...filtered.credentials } as Record<
      string,
      unknown
    >;
    const credentialProperties = schema.properties.credentials.properties;

    Object.keys(credentialErrors).forEach((key) => {
      if (isConstSchemaProperty(credentialProperties[key])) {
        delete credentialErrors[key];
      }
    });

    if (Object.keys(credentialErrors).length === 0) {
      delete filtered.credentials;
    } else {
      filtered.credentials =
        credentialErrors as FieldErrors<FormData>["credentials"];
    }
  }

  return filtered;
}

export interface ProviderCapabilities {
  isPriorityEditable: boolean;
  priorityFixedValue: number;
  hasBrands: boolean;
  brandsRequired: boolean;
  hasCredentials: boolean;
  credentialsRequired: boolean;
  hasMerchantId: boolean;
  showMcc: boolean;
  availableMethods: string[];
  availableBrands: string[];
}

export function getProviderCapabilities(
  provider: AvailableProvider | null,
): ProviderCapabilities {
  const schema = provider?.schema;
  return {
    isPriorityEditable: isPriorityEditable(schema),
    priorityFixedValue: getPriorityFixedValue(schema),
    hasBrands: hasBrandsInSchema(schema),
    brandsRequired: areBrandsRequired(schema),
    hasCredentials: hasCredentialsInSchema(schema),
    credentialsRequired: areCredentialsRequired(schema),
    hasMerchantId: hasMerchantIdInSchema(schema),
    showMcc: shouldShowMcc(schema),
    availableMethods: getAvailableMethods(schema),
    availableBrands: getAvailableBrands(schema),
  };
}
